import React from 'react'
import { useNavigate } from 'react-router'
import {
  Button,
  Col,
  Container,
  Row,
  Card,
  CardBody,
  CardTitle,
  CardText,
  ListGroup,
  ListGroupItem
} from 'reactstrap'
import { getMixpanel } from '../../api/mixpanel'
import '../landing-page/index.scss'
import './index.scss'

// Import images (you'll need to add these to your assets)
import logo from '../../images/logo.svg'
import pmfscore from '../../images/pmfscore.png'
import vivino from '../../images/logos/vivino.svg'
import zerofy from '../../images/logos/zerofy.svg'
import earkick from '../../images/logos/earkick.svg'

const mixpanel = getMixpanel()

const FeatureCard = ({ icon, title, description }) => (
  <Card className="feature-card h-100">
    <CardBody>
      <div className="feature-icon">{icon}</div>
      <CardTitle tag="h5">{title}</CardTitle>
      <CardText>{description}</CardText>
    </CardBody>
  </Card>
)

const LandingPage2 = () => {
  const navigate = useNavigate()

  const handleStartFree = () => {
    mixpanel.track('L02 Landing page - Start Free button click')
    navigate('/onboarding')
  }

  return (
    <div className="landing-page landing-page-2">
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="header">
            <Col>
              <img src={logo} alt="PMF-Engine" className="logo" />
            </Col>
            <Col className="login-box">
              <Button onClick={() => {
                mixpanel.track('L02 Landing page - Login button click')
                navigate('/login')
              }}>Login</Button>
            </Col>
          </Row>

          <Row className="hero-content align-items-center">
            <Col lg={6} className="hero-text">
              <h1>Product Market Fit Survey Tool That Actually Works</h1>
              <h2>Discover what your target audience really needs using our AI-powered product market fit survey.</h2>
              <p className="lead">
                Inspired by Superhuman’s method, PMF-Engine helps companies, entrepreneurs, and potential customers
                reach product-market fit with automated customer feedback, instant insights, and AI-powered decisions.
                The ultimate survey tool to validate your viable product with confidence.
              </p>
              <Button
                color="primary"
                size="lg"
                className="cta-button"
                onClick={handleStartFree}
              >
                Start Free – No Credit Card Needed
              </Button>
            </Col>
            <Col lg={6} className="hero-visual">
              <img src={pmfscore} alt="PMF Score Dashboard" className="img-fluid" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Trust Logos Section */}
      <section className="trust-section">
        <Container>
          <p className="text-center trust-text">Trusted by product teams at</p>
          <Row className="justify-content-center align-items-center">
            <Col className="customer-logo">
              <img src={vivino} alt="Vivino" />
            </Col>
            <Col className="customer-logo">
              <img src={zerofy} alt="Zerofy" />
            </Col>
            <Col className="customer-logo">
              <img src={earkick} alt="Earkick" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* PMF Question Section */}
      <section className="pmf-question-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <h2>PMF Survey Questions That Reveal Real Demand</h2>
              <p className="lead">
              Based on Sean Ellis&apos; proven fit survey question — &ldquo;How would you feel if you could no longer use this product?&rdquo; —
              PMF-Engine delivers clear, instant pmf survey results to improve customer satisfaction, experience, and overall product success.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <Container>
          <Row>
            <Col md={12}>
              <h2>AI Insights & Experience-Driven Research</h2>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <FeatureCard
                icon="📊"
                title="Automated Segmentation"
                description="Identify which respondents love, like, or are indifferent to your product."
              />
            </Col>
            <Col md={4}>
              <FeatureCard
                icon="📈"
                title="Live PMF Score Tracking"
                description="Continuously measure your market fit score, and benchmark your growth."
              />
            </Col>
            <Col md={4}>
              <FeatureCard
                icon="🤖"
                title="AI-Powered Insights"
                description="Discover which features drive delight—and which ones don't."
              />
            </Col>
          </Row>
          <Row className="mt-4">
            <Col md={6}>
              <FeatureCard
                icon="⚡"
                title="Plug-and-Play SDK"
                description="Add to your product in minutes, zero dev needed."
              />
            </Col>
            <Col md={6}>
              <FeatureCard
                icon="🎯"
                title="Actionable Roadmap Recommendations"
                description="Based on survey results and real feedback."
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* PMF Score Section */}
      <section className="pmf-score-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <img src={pmfscore} alt="PMF Score Chart" className="img-fluid" />
            </Col>
            <Col lg={6}>
              <div className="testimonial-box">
                <blockquote>
                  &ldquo;PMF-Engine helped us go from 23% to 41% &apos;very disappointed&apos; users in just 2 months.&rdquo;
                </blockquote>
                <cite>– Head of Product @ [Startup]</cite>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section>
        <Container>
          <Row>
            <Col md={12}>
              <h2>Fit Survey Templates to Save Time</h2>
              <p>Don’t start from scratch. Choose from proven survey templates that simplify setup:</p>
              <ul>
                <li>Fit survey template based on Sean Ellis&apos; research</li>
                <li>Quick-start survey example formats for Typeform, Satismeter, or native PMF-Engine SDK</li>
                <li>Pre-written questions optimized for high-quality survey responses</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <Container>
          <h2 className="text-center mb-5">Step Guide: How PMF Surveys Improve Your Business</h2>
          <Row>
            <Col md={4}>
              <div className="step-box">
                <div className="step-number">1</div>
                <h3>Install Lightweight Widget</h3>
                <p>Copy-paste a code snippet or use the SDK.</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="step-box">
                <div className="step-number">2</div>
                <h3>Use Fit Survey Questions That Matter</h3>
                <p>Get qualitative and quantitative feedback using short surveys in a proven survey format.</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="step-box">
                <div className="step-number">3</div>
                <h3>Let AI Guide Product Development</h3>
                <p>Understand your target customers, discover market needs, and plan the perfect product.</p>
                <p>Perfect for early-stage startups and mature teams alike.
                  This step-by-step process helps you identify pain points, refine your product, and achieve stronger market fit.</p>
              </div>
            </Col>
          </Row>
          <div className="text-center mt-5">
            <Button color="primary" size="lg" onClick={handleStartFree}>
              Get Started for Free
            </Button>
          </div>
        </Container>
      </section>

      {/* Quote Section */}
      <section className="quote-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <blockquote className="methodology-quote">
                &ldquo;If over 40% of your users say they&apos;d be very disappointed without your product,
                you&apos;ve found product-market fit.&rdquo;
                <cite>– Sean Ellis</cite>
              </blockquote>
              <p className="methodology-note">
                Built on Superhuman&apos;s methodology. Supercharged with AI.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <Container>
          <h2 className="text-center mb-5">Simple Pricing</h2>
          <h2>Built for Modern Brands and Teams</h2>
          <p>
            Used by teams at Vivino, Zerofy, Earkick, and more. Whether you’re building a product or service,
            launching a brand, or testing advertising, PMF-Engine helps you stand out from your competitors.
          </p>
          <h2>Why Our PMF-Engine Beats SurveyMonkey or NPS</h2>
          <p>
            Unlike traditional tools like SurveyMonkey, PMF-Engine uses a refined survey format designed to
            track customer experience, demand, and customer lifetime value. Understand your sample size,
            the percentage of happy customers, and better target your consumers.
          </p>
          <Row>
            <Col md={6}>
              <Card className="pricing-card">
                <CardBody>
                  <CardTitle tag="h3">Free Tier</CardTitle>
                  <ListGroup flush>
                    <ListGroupItem>1 active survey</ListGroupItem>
                    <ListGroupItem>Up to 500 responses/month</ListGroupItem>
                    <ListGroupItem>PMF Score dashboard</ListGroupItem>
                  </ListGroup>
                  <Button color="primary" block onClick={handleStartFree}>
                    Start for Free
                  </Button>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="pricing-card">
                <CardBody>
                  <CardTitle tag="h3">Pro Tier</CardTitle>
                  <ListGroup flush>
                    <ListGroupItem>Unlimited responses</ListGroupItem>
                    <ListGroupItem>Full AI insights & trends</ListGroupItem>
                    <ListGroupItem>Team collaboration</ListGroupItem>
                    <ListGroupItem>Export capabilities</ListGroupItem>
                  </ListGroup>
                  <Button color="primary" block onClick={handleStartFree}>
                    Get Pro
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <Container>
          <h2 className="text-center mb-5">Frequently Asked Questions</h2>
          <Row>
            <Col lg={8} className="mx-auto">
              <Card className="faq-card">
                <CardBody>
                  <h3>How is this different from NPS or other surveys?</h3>
                  <p>PMF-Engine is a research-backed solution that predicts product-market fit more accurately than NPS.</p>

                  <h3>Do I need a developer to install it?</h3>
                  <p>Yes, but integration is quick and easy. We&apos;ll help you set it up.</p>

                  <h3>Is my data private?</h3>
                  <p>Yes, your data is encrypted and never shared.</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <h2>Ready to Reach Product-Market Fit?</h2>
              <p className="lead">
              Start using PMF-Engine today. Validate your ideas, learn from your target market, and convert more
              potential customers with smart, AI-driven decisions.
              </p>
              <Button color="primary" size="lg" onClick={handleStartFree}>
                Start Free – No Credit Card Needed
              </Button>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  )
}

export default LandingPage2
